import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import { AuthHeading, PrimaryButton, ErrorBanner } from '../../components/auth/AuthPrimitives';
import billingApi from '../../utils/billingApi';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Real Stripe Checkout: no card data is collected in-app. We create a Checkout
// Session via the existing billing endpoint and hand the browser to Stripe's
// hosted, PCI-scoped checkout. The prototype's mock card form is design-reference
// only. Stripe returns to the backend-configured success/cancel URLs
// (STRIPE_SUCCESS_URL -> /billing/success, which routes onward to /overview).

function SumRow({ label, val, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', color: muted ? '#64748B' : '#000' }}>
      <span>{label}</span>
      <span>{val}</span>
    </div>
  );
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subtotal = 29.99;
  const total = subtotal;
  const today = new Date();
  const renew = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  async function handlePay() {
    setLoading(true);
    setError('');
    try {
      // No plan_slug: the backend uses its configured default monthly price
      // (STRIPE_PRICE_ID_MONTHLY), avoiding a hard dependency on a synced
      // SubscriptionPlan row in every environment.
      const data = await billingApi.createCheckoutSession();
      if (data?.url) {
        window.location.href = data.url; // eslint-disable-line no-restricted-properties -- real Stripe Checkout redirect
      } else {
        setError('We could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'We could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <AuthShell onLogoClick={() => navigate('/login')} wide>
      <AuthHeading
        eyebrow="Step 3 of 3 · Payment"
        title="Confirm your subscription"
        subtitle="Review your order. We’ll redirect you to Stripe to complete payment securely."
      />

      <ErrorBanner>{error}</ErrorBanner>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
        <div style={{ border: '1px solid #000', background: '#fff' }}>
          <div style={{ padding: '16px 24px', borderBottom: '0.4px solid #000', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, margin: 0 }}>Order summary</h3>
            <a onClick={() => navigate('/plans')} style={{ fontFamily: FONT, fontSize: 13, color: '#000', textDecoration: 'underline', cursor: 'pointer' }}>Change plan</a>
          </div>
          <div style={{ padding: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B' }}>Most popular</div>
              <div style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, marginTop: 4 }}>Meduri Practice</div>
              <div style={{ fontFamily: FONT, fontSize: 14, color: '#1D2433', marginTop: 8, lineHeight: 1.5, maxWidth: 360 }}>
                Unlimited generated appeals, HIPAA-compliant fax, outcome tracking, and the LMN generator.
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700 }}>$29.99</div>
              <div style={{ fontFamily: FONT, fontSize: 12, color: '#64748B' }}>per month</div>
            </div>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '0.4px solid rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT, fontSize: 14 }}>
            <SumRow label="Subtotal" val={`$${subtotal.toFixed(2)}`} />
            <SumRow label="Tax" val="Calculated by Stripe" muted />
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid #000', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: '#F8F9FC' }}>
            <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700 }}>Due today</span>
            <span style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700 }}>${total.toFixed(2)}</span>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '0.4px solid rgba(0,0,0,0.12)', fontFamily: FONT, fontSize: 12, color: '#64748B', lineHeight: 1.55 }}>
            Renews {fmt(renew)} for $29.99/mo. Cancel anytime from Billing. No prorated charges if you switch plans mid-month.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ border: '1px solid #000', padding: 24, background: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontFamily: FONT, fontSize: 13, color: '#1D2433', lineHeight: 1.5 }}>
              You’ll be redirected to Stripe’s secure checkout to enter your card.
              We don’t store card details on our servers.
            </div>
            <PrimaryButton onClick={handlePay} loading={loading} icon={Lock}>{'Pay with Stripe · $29.99'}</PrimaryButton>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontFamily: FONT, fontSize: 11, color: '#64748B' }}>
              <ShieldCheck size={14} />
              <span>PCI-DSS compliant. 256-bit TLS. Powered by Stripe.</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate('/plans')}
            style={{ fontFamily: FONT, fontSize: 13, background: 'transparent', border: 'none', color: '#64748B', textDecoration: 'underline', cursor: 'pointer', padding: 8, alignSelf: 'center' }}
          >
            Back to plans
          </button>
        </div>
      </div>
    </AuthShell>
  );
}
